import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Search, Calendar, Layers } from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";

export default function Sets() {
  const { t, i18n } = useTranslation();
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [orderBy, setOrderBy] = useState("-releaseDate");

  useEffect(() => {
    setLoading(true);
    api.get("/sets", { params: { pageSize: 200, orderBy, q: q || undefined } })
      .then((r) => setSets(r.data.data || []))
      .finally(() => setLoading(false));
  }, [orderBy, q, i18n.language]);

  return (
    <div className="page-enter max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
      <Seo
        title="All Pokémon TCG sets"
        description="Browse every Pokémon TCG set ever released — Base Set to Scarlet & Violet. Multi-language localized names, release dates, full set listings."
        url={window.location.origin + "/sets"}
      />
      <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
        <div>
          <div className="text-xs font-semibold text-[#E11D48] uppercase tracking-wider mb-2">{t("sets.label")}</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight text-slate-900">{t("sets.title")}</h1>
          <p className="text-slate-500 mt-2 max-w-xl">{t("sets.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              data-testid="sets-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("sets.filter_placeholder")}
              className="w-full pl-9"
            />
          </div>
          <select data-testid="sets-order-select" value={orderBy} onChange={(e) => setOrderBy(e.target.value)} className="text-sm">
            <option value="-releaseDate">{t("sets.newest")}</option>
            <option value="releaseDate">{t("sets.oldest")}</option>
            <option value="name">{t("sets.az")}</option>
            <option value="-name">{t("sets.za")}</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-slate-400 py-20 text-sm">{t("sets.loading_sets")}</div>
      ) : sets.length === 0 ? (
        <div className="text-center text-slate-400 py-20">{t("sets.no_sets")}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
          {sets.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: Math.min(i, 18) * 0.025 }}
            >
              <Link
                to={`/sets/${s.id}`}
                data-testid={`set-card-${s.id}`}
                className="flat-card p-5 flex flex-col gap-4 min-h-[180px] relative shimmer h-full"
              >
                <div className="h-20 flex items-center">
                  {s.images?.logo ? (
                    <img src={s.images.logo} alt={s.name} className="max-h-16 max-w-full object-contain" />
                  ) : (
                    <Layers className="w-10 h-10 text-slate-300" />
                  )}
                </div>
                <div className="mt-auto relative z-10">
                  <div className="font-heading font-bold text-base tracking-tight leading-tight text-slate-900">{s.name}</div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {s.releaseDate}</span>
                    <span>· {s.total} {t("sets.cards")}</span>
                  </div>
                  {s.series && <div className="text-[10px] text-[#E11D48] mt-1 font-semibold uppercase tracking-wide">{s.series}</div>}
                </div>
                {s.images?.symbol && (
                  <img src={s.images.symbol} alt="" className="absolute top-4 right-4 w-6 h-6 opacity-60" />
                )}
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
